import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { apiErrorMessage } from '../../../lib/apiError';
import { MODULE_LABELS } from '../../../lib/modules';
import { CsvButton } from './export';
import { getStatistics } from '../../../api/statistics';
// ---------------------------------------------------------------- Libellés
export const PLATFORM_COLORS = { ios: '#3B82F6', android: '#10B981', web: '#F59E0B' };
export const PLATFORM_LABELS = { ios: 'iOS', android: 'Android', web: 'Site internet' };
const EXTRA_LABELS = {
    accueil: 'Accueil',
    services: 'Services',
    compte: 'Compte',
    wallet: 'Wallet',
    recherche: 'Recherche',
    carte: 'Carte',
    vigilances: 'Vigilances',
    legal: 'Pages légales',
    general: 'Général',
};
/** Clé d'écran, de page du site ou de catégorie de notification. */
export function keyLabel(key) {
    return EXTRA_LABELS[key] ?? MODULE_LABELS[key] ?? key;
}
export const CONTENT_TYPE_LABELS = {
    article: 'Article',
    event: 'Événement',
    project: 'Projet',
    poll: 'Sondage',
    directory: 'Fiche annuaire',
    dataset: 'Données',
    alert: 'Alerte',
    form: 'Démarche',
};
export const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
// ---------------------------------------------------------------- Formats
const intFormat = new Intl.NumberFormat('fr-FR');
const decFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
export function fmt(n) {
    if (n === null || n === undefined)
        return '-';
    return Number.isInteger(n) ? intFormat.format(n) : decFormat.format(n);
}
export function fmtPct(n) {
    return n === null || n === undefined ? '-' : `${decFormat.format(n)} %`;
}
/** Délai en heures : en heures sous 48 h, en jours au-delà. */
export function fmtHours(h) {
    if (h === null || h === undefined)
        return '-';
    if (h < 1)
        return `${Math.max(1, Math.round(h * 60))} min`;
    if (h < 48)
        return `${decFormat.format(h)} h`;
    return `${decFormat.format(h / 24)} j`;
}
export function fmtDays(d) {
    return d === null || d === undefined ? '-' : `${decFormat.format(d)} j`;
}
/** Étiquette d'une tranche de courbe selon la granularité. */
export function bucketLabel(date, granularity, long = false) {
    const d = parseISO(date);
    if (granularity === 'month')
        return format(d, 'MMM yyyy', { locale: fr });
    if (granularity === 'week')
        return `${long ? 'Semaine du ' : 'Sem. '}${format(d, 'd MMM', { locale: fr })}`;
    return format(d, long ? 'EEEE d MMMM yyyy' : 'd MMM', { locale: fr });
}
export function periodLabel(from, to) {
    return `du ${format(parseISO(from), 'd MMMM yyyy', { locale: fr })} au ${format(parseISO(to), 'd MMMM yyyy', { locale: fr })}`;
}
/** Évolution en % par rapport à la période précédente, null si incalculable. */
export function delta(current, previous) {
    if (current === null || current === undefined || previous === null || previous === undefined || previous === 0)
        return null;
    return Math.round(((current - previous) / previous) * 1000) / 10;
}
// ---------------------------------------------------------------- Données
export function useStatistics(section, q, extra, enabled = true) {
    const { impersonated } = useImpersonation();
    return useQuery({
        queryKey: ['statistics', impersonated?.slug, section, q, extra],
        queryFn: () => getStatistics(section, q, extra),
        placeholderData: keepPreviousData,
        staleTime: 60000,
        enabled,
    });
}
// ---------------------------------------------------------------- Composants
export function KpiCard({ label, value, sub, change, lowerIsBetter = false, icon, accent = 'bg-blue-50 text-blue-600' }) {
    const good = change !== null && change !== undefined && (lowerIsBetter ? change < 0 : change > 0);
    const bad = change !== null && change !== undefined && (lowerIsBetter ? change > 0 : change < 0);
    return (_jsxs("div", { className: "bg-white rounded-xl border border-gray-100 shadow-sm p-4", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [icon && _jsx("div", { className: `w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent}`, children: icon }), change !== undefined && (_jsxs("span", { className: `flex items-center gap-1 text-xs font-semibold ${good ? 'text-emerald-600' : bad ? 'text-red-500' : 'text-gray-400'}`, title: "Par rapport \u00E0 la p\u00E9riode pr\u00E9c\u00E9dente", children: [change === null ? _jsx(Minus, { size: 12 }) : change > 0 ? _jsx(TrendingUp, { size: 12 }) : change < 0 ? _jsx(TrendingDown, { size: 12 }) : _jsx(Minus, { size: 12 }), change === null ? 'n.c.' : `${change > 0 ? '+' : ''}${fmt(change)} %`] }))] }), _jsx("p", { className: "text-2xl font-bold text-gray-900 mt-3 leading-none", children: value }), _jsx("p", { className: "text-[13px] text-gray-500 font-medium mt-1", children: label }), sub && _jsx("p", { className: "text-[11px] text-gray-400 mt-0.5", children: sub })] }));
}
export function KpiGrid({ children }) {
    return _jsx("div", { className: "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6", children: children });
}
export function ChartCard({ title, subtitle, children, className = '', height, csv }) {
    return (_jsxs("section", { className: `bg-white rounded-xl border border-gray-100 shadow-sm p-5 break-inside-avoid ${className}`, children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("h3", { className: "text-sm font-semibold text-gray-800", children: title }), subtitle && _jsx("p", { className: "text-xs text-gray-400 mt-0.5", children: subtitle })] }), csv && _jsx(CsvButton, { table: csv })] }), _jsx("div", { className: "mt-4", style: height ? { height } : undefined, children: children })] }));
}
export function Empty({ children = 'Aucune donnée sur la période.' }) {
    return _jsx("div", { className: "h-full min-h-[120px] flex items-center justify-center text-sm text-gray-400 text-center px-4", children: children });
}
export function ErrorBox({ error, onRetry }) {
    return (_jsxs("div", { className: "bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-center gap-3 text-sm", children: [_jsx(AlertCircle, { size: 18, className: "shrink-0" }), _jsx("span", { className: "flex-1", children: apiErrorMessage(error, 'Impossible de charger les statistiques.') }), _jsx("button", { onClick: onRetry, className: "px-3 py-1.5 rounded-lg bg-white border border-red-200 font-medium hover:bg-red-100", children: "R\u00E9essayer" })] }));
}
export function Loading() {
    return _jsx("div", { className: "py-16 text-center text-sm text-gray-400", children: "Chargement des statistiques..." });
}
/** Classement horizontal simple (barres en CSS), lisible même avec de longs libellés. */
export function RankList({ rows, empty }) {
    if (rows.length === 0)
        return _jsx(Empty, { children: empty });
    const max = Math.max(...rows.map((r) => r.value), 1);
    return (_jsx("ul", { className: "space-y-2", children: rows.map((r, i) => {
            const inner = (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-baseline justify-between gap-3 text-[13px]", children: [_jsx("span", { className: "text-gray-700 truncate", children: r.label }), _jsx("span", { className: "font-semibold text-gray-900 tabular-nums shrink-0", children: fmt(r.value) })] }), _jsx("div", { className: "h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden", children: _jsx("div", { className: "h-full bg-blue-500 rounded-full", style: { width: `${(r.value / max) * 100}%` } }) }), r.hint && _jsx("p", { className: "text-[11px] text-gray-400 mt-0.5", children: r.hint })] }));
            return (_jsx("li", { children: r.onClick ? (_jsx("button", { type: "button", onClick: r.onClick, className: "w-full text-left rounded-lg -mx-2 px-2 py-1 hover:bg-gray-50", children: inner })) : inner }, i));
        }) }));
}
/** Infobulle commune aux graphiques : libellé long de la tranche. */
export function tooltipLabel(granularity) {
    return (label) => (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(label) ? bucketLabel(label, granularity, true) : String(label));
}
function toNumber(v) {
    const n = typeof v === 'number' ? v : Number(v);
    return v === null || v === undefined || Number.isNaN(n) ? null : n;
}
/** Valeur d'infobulle : nombre au format français, nom de la série ou nom imposé. */
export function tip(name, format = fmt) {
    return (value, series) => [format(toNumber(value)), name ?? String(series)];
}
export const AXIS_PROPS = { tick: { fontSize: 11, fill: '#9CA3AF' }, tickLine: false, axisLine: false };
